import type { SearchSelectOption, SearchSelectPage } from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';
import type { AdminMemberSummary } from '@/features/users/hooks/useAdminUsersQuery';

/**
 * ID55 주문자 회원 검색 로더. catalog/order 가 아닌 user-service admin 목록
 * (/api/admin/v1/users) 을 사용한다. SearchSelectField(single) 의 옵션으로 회원을
 * 매핑한다. value=userId, label=이름, hint=이메일·연락처.
 */
const PAGE_SIZE = 20;

interface AdminMemberListEnvelope {
  success: boolean;
  data?: {
    items?: AdminMemberSummary[];
    content?: AdminMemberSummary[];
    totalElements?: number;
    meta?: { total?: number };
  } | null;
}

const toOption = (m: AdminMemberSummary): SearchSelectOption => ({
  value: m.userId,
  label: m.name,
  hint: [m.email, m.mobileNumber].filter(Boolean).join(' · ') || undefined,
});

export const searchMembers = async (
  keyword: string,
  page: number,
): Promise<SearchSelectPage> => {
  const q = new URLSearchParams();
  const trimmed = keyword.trim();
  if (trimmed) q.set('keyword', trimmed);
  q.set('page', String(page));
  q.set('size', String(PAGE_SIZE));
  q.set('unmask', 'true');

  const res = await adminFetch(`/api/admin/v1/users?${q.toString()}`);
  if (!res.ok) throw new Error(`member search failed: ${res.status}`);
  const env = (await res.json()) as AdminMemberListEnvelope;
  const d = env.data;
  const rawItems = d?.items ?? d?.content ?? [];
  const items = rawItems.map(toOption);

  const total = d?.meta?.total ?? d?.totalElements;
  const hasMore =
    total != null ? (page + 1) * PAGE_SIZE < total : items.length === PAGE_SIZE;

  return total != null ? { items, hasMore, total } : { items, hasMore };
};

export const resolveMember = async (
  userId: string,
): Promise<SearchSelectOption | null> => {
  try {
    const res = await adminFetch(`/api/admin/v1/users/${userId}?unmask=true`);
    if (!res.ok) return null;
    const env = (await res.json()) as { data?: AdminMemberSummary | null };
    if (!env.data) return null;
    return toOption(env.data);
  } catch {
    return null;
  }
};
